import { Button, Heading, Icon, Link, RadioCard, RadioList, Text } from "@oysterhr/ui"
import { useState } from "react"
import { SepaFteOverview } from "../SepaFteOverview"
import { SepaContractorOverview } from "../SepaContractorOverview"
import { useNavigate } from "react-router-dom"
import { useAccountAspect } from "@oysterhr/identity/AccountAspectContext"
import { companySepaMethodTypePath } from "@oysterhr/router"
import { Trans, useTranslation } from "react-i18next"
import type { TFunction } from "react-i18next"
import { useNewCompanyPaymentMethods } from "../../../../../hooks/useNewCompanyPaymentMethods"
import { PaymentMethodCategoryEnumType } from "../../../../../enums"
import { HOW_TO_SET_UP_DIRECT_DEBIT_LINK } from "../../../../../utils"

export const SepaMethodSelection = () => {
  const { t } = useTranslation("company_interface/CompanyPaymentMethodsPage/SepaFlow", {
    keyPrefix: "methodSelection",
  })
  const navigate = useNavigate()
  const { accountAspect: companyId } = useAccountAspect()
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethodCategoryEnumType | undefined>()
  const [selectionError, setSelectionError] = useState(false)

  const { data } = useNewCompanyPaymentMethods({ variables: { companyId } })

  const onSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedMethod(e.target.value as PaymentMethodCategoryEnumType)
    setSelectionError(false)
  }

  const onContinue = () => {
    if (selectedMethod) {
      navigate(companySepaMethodTypePath(companyId, selectedMethod))
    } else {
      setSelectionError(true)
    }
  }

  const hasContractorPaymentMethod = Boolean(data?.company?.paymentMethod)
  const hasFTEPaymentMethod = Boolean(data?.company?.customerBankAccount)

  const selectedMethodAlreadyExists =
    (selectedMethod === PaymentMethodCategoryEnumType.Fte && hasFTEPaymentMethod) ||
    (selectedMethod === PaymentMethodCategoryEnumType.Contractor && hasContractorPaymentMethod)

  return (
    <div className="text-left">
      <Heading size="lg">{t("header")}</Heading>
      <RadioList
        name="paymentMethod"
        orientation="horizontal"
        innerClassName="mt-4 space-x-2"
        onChange={onSelect}
      >
        <RadioCard
          key={PaymentMethodCategoryEnumType.Fte}
          id={PaymentMethodCategoryEnumType.Fte}
          value={PaymentMethodCategoryEnumType.Fte}
          checked={selectedMethod === PaymentMethodCategoryEnumType.Fte}
          label={
            <RadioCard.Label hint={t("FTE.hint")} id="fte">
              {t("FTE.label")}
            </RadioCard.Label>
          }
        />
        <RadioCard
          key={PaymentMethodCategoryEnumType.Contractor}
          id={PaymentMethodCategoryEnumType.Contractor}
          value={PaymentMethodCategoryEnumType.Contractor}
          checked={selectedMethod === PaymentMethodCategoryEnumType.Contractor}
          label={
            <RadioCard.Label hint={t("contractor.hint")} id="contractor">
              {t("contractor.label")}
            </RadioCard.Label>
          }
        />
      </RadioList>

      {selectedMethod === PaymentMethodCategoryEnumType.Fte ? (
        hasFTEPaymentMethod ? (
          <PaymentAdded paymentType={PaymentMethodCategoryEnumType.Fte} t={t} />
        ) : (
          <SepaFteOverview />
        )
      ) : null}

      {selectedMethod === PaymentMethodCategoryEnumType.Contractor ? (
        hasContractorPaymentMethod ? (
          <PaymentAdded paymentType={PaymentMethodCategoryEnumType.Contractor} t={t} />
        ) : (
          <SepaContractorOverview />
        )
      ) : null}

      {selectedMethod && !selectedMethodAlreadyExists && (
        <footer className="mt-6 space-y-6">
          <Trans
            t={t}
            i18nKey="footer.details"
            components={{
              a: (
                <Link
                  to={HOW_TO_SET_UP_DIRECT_DEBIT_LINK}
                  className="inline-block"
                  target="_blank"
                />
              ),
            }}
          />
          <div className="space-y-2">
            <Heading size="lg">{t("footer.info.header")}</Heading>
            <Text>{t("footer.info.content")}</Text>
          </div>
        </footer>
      )}

      {selectionError && <Text className="mt-3 text-error-base">{t("validation")}</Text>}

      <Button className="mt-6" onClick={onContinue} disabled={selectedMethodAlreadyExists}>
        {t("btnContinue")}
      </Button>
    </div>
  )
}

interface PaymentAddedProps {
  paymentType: PaymentMethodCategoryEnumType
  t: TFunction
}

const PaymentAdded = ({ paymentType, t }: PaymentAddedProps) => {
  return (
    <div className="mt-6 flex flex-col items-center justify-center space-y-3 rounded-lg border border-gray-200 py-10 text-center">
      <Icon name="CheckCircle" className="text-success-base" size="md" />
      <div className="px-20">
        <Heading size="lg">
          {t("paymentAdded.title", {
            paymentType:
              paymentType === PaymentMethodCategoryEnumType.Fte
                ? paymentType.toUpperCase()
                : paymentType,
          })}
        </Heading>
        <Text className="text-gray-400">{t(`paymentAdded.content.${paymentType}`)}</Text>
      </div>
    </div>
  )
}
